import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function ProtectFromSpoofingGuide() {
  return (
    <div>
      <SEO
        title="Protect Your Business from Email Spoofing"
        description="Learn how scammers impersonate your domain to send fake emails and how DMARC stops them. Protect your brand reputation and prevent Business Email Compromise (BEC) attacks."
        canonical="/guides/protect-from-spoofing"
        ogType="article"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Help Center
          </Link>
          <h1 className="mt-8 text-4xl font-bold tracking-tight sm:text-5xl">
            Protect Your Business from Email Spoofing
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
            How scammers impersonate your domain and how to stop them.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">

          {/* Section 1 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              What is Email Spoofing?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Email spoofing is when someone sends an email that appears to come from your domain,
              but actually comes from a completely different source. The email looks legitimate —
              it has your company name, your domain, maybe even a real employee's name — but it's
              fake.
            </p>

            <div className="mt-8 rounded-2xl border-l-4 border-red-500 bg-red-50 p-6 dark:bg-red-950/30">
              <h3 className="font-semibold text-red-800 dark:text-red-200">Real Example</h3>
              <p className="mt-2 text-red-700 dark:text-red-300">
                A scammer sends an email that appears to be from <strong>accounting@yourcompany.com</strong>{" "}
                to one of your vendors, asking them to update their payment details to a new bank account.
                The vendor thinks it's you and sends the next payment to the scammer.
              </p>
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                This is called Business Email Compromise (BEC), and it costs businesses billions
                of dollars every year.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 2 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Why Should You Care?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Even if you're not directly scammed, spoofing hurts your business:
            </p>

            <div className="mt-8 space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Your reputation suffers</h3>
                  <p className="mt-1 text-muted-foreground">
                    When customers receive fake emails "from" you, they lose trust in your brand.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Your domain gets blacklisted</h3>
                  <p className="mt-1 text-muted-foreground">
                    If scammers use your domain for spam, email providers may start blocking ALL email from your domain.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Legal liability</h3>
                  <p className="mt-1 text-muted-foreground">
                    Depending on your industry, you may be required to have email authentication in place.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Lost business</h3>
                  <p className="mt-1 text-muted-foreground">
                    Customers who receive spam "from" you may unsubscribe or mark you as spam, hurting future legitimate emails.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 3 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              How Scammers Spoof Your Domain
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Here's the scary part: it's incredibly easy. Email was designed in the 1970s without
                security in mind. The "From" address in an email is just text that anyone can set to
                anything.
              </p>
              <p>
                Scammers use simple tools to send emails that look like they come from your domain.
                Without email authentication, receiving email servers have no way to know the email
                is fake.
              </p>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 4 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              How DMARC Stops Spoofing
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              DMARC (Domain-based Message Authentication, Reporting & Conformance) is the solution.
              Here's how it works:
            </p>

            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">1</div>
                <p className="mt-2 font-semibold">You publish a policy</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  A DNS record that says "emails from my domain must pass authentication"
                </p>
              </div>
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">2</div>
                <p className="mt-2 font-semibold">Email providers check</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Gmail, Outlook, etc. verify incoming emails against your policy
                </p>
              </div>
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">3</div>
                <p className="mt-2 font-semibold">Fake emails get blocked</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Emails that fail authentication are rejected or sent to spam
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 5 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              The Three DMARC Policies
            </h2>

            <div className="mt-10 space-y-4">
              <div className="rounded-2xl border bg-amber-50 p-6 dark:bg-amber-950/30">
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-amber-200 px-3 py-1 text-sm font-bold text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                    p=none
                  </span>
                  <span className="font-semibold text-lg">Monitor Only</span>
                </div>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  Start here. Emails are delivered normally, but you receive reports about who's
                  sending email as your domain. Use this to discover legitimate senders before
                  enforcing.
                </p>
              </div>

              <div className="rounded-2xl border bg-orange-50 p-6 dark:bg-orange-950/30">
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-orange-200 px-3 py-1 text-sm font-bold text-orange-800 dark:bg-orange-800 dark:text-orange-200">
                    p=quarantine
                  </span>
                  <span className="font-semibold text-lg">Send to Spam</span>
                </div>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  Emails that fail authentication go to the spam folder. A good middle ground
                  while you're still configuring.
                </p>
              </div>

              <div className="rounded-2xl border bg-green-50 p-6 dark:bg-green-950/30">
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-green-200 px-3 py-1 text-sm font-bold text-green-800 dark:bg-green-800 dark:text-green-200">
                    p=reject
                  </span>
                  <span className="font-semibold text-lg">Block Completely</span>
                </div>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  The strongest protection. Emails that fail authentication are rejected entirely
                  and never delivered. This is your goal.
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-12 border-border" />

          {/* Section 6 */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Getting Started
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              The key is to start with monitoring (<code className="rounded bg-muted px-2 py-0.5">p=none</code>) so you can see who's
              sending email as your domain before you start blocking. GetSent helps you:
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold dark:bg-indigo-900/30 dark:text-indigo-300">
                  1
                </div>
                <span className="text-lg text-muted-foreground">Set up your initial DMARC record in monitoring mode</span>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold dark:bg-indigo-900/30 dark:text-indigo-300">
                  2
                </div>
                <span className="text-lg text-muted-foreground">Receive and visualize DMARC reports</span>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold dark:bg-indigo-900/30 dark:text-indigo-300">
                  3
                </div>
                <span className="text-lg text-muted-foreground">Identify all legitimate email sources</span>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold dark:bg-indigo-900/30 dark:text-indigo-300">
                  4
                </div>
                <span className="text-lg text-muted-foreground">
                  Gradually increase enforcement to <code className="rounded bg-muted px-2 py-0.5">p=reject</code>
                </span>
              </div>
            </div>

            <div className="mt-10 flex justify-center">
              <Link
                to="/tools"
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-105"
              >
                Check If You're Protected
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </section>

          {/* CTA Box */}
          <section className="rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 p-8 dark:from-indigo-950/50 dark:to-purple-950/50 sm:p-10">
            <h3 className="text-2xl font-bold">Start Protecting Your Domain Today</h3>
            <p className="mt-4 text-lg text-muted-foreground">
              GetSent monitors your DMARC reports and helps you understand who's sending email
              as your domain — so you can block the bad actors while ensuring legitimate emails
              get through.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white hover:bg-indigo-500 transition-colors"
              >
                Get Started Free
              </Link>
              <Link
                to="/resources"
                className="inline-flex rounded-lg border border-border bg-background px-6 py-3 text-base font-semibold hover:bg-muted transition-colors"
              >
                Explore More Guides
              </Link>
            </div>
          </section>

        </div>
      </article>
    </div>
  );
}
